import React, { useState, useRef, useEffect } from 'react';

interface SearchableDropdownProps {
  options: readonly string[] | string[];
  selectedValue: string;
  onChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  className?: string;
  error?: boolean;
  disabled?: boolean;
  position?: 'auto' | 'below' | 'above';
  onOpen?: () => void;
  dropdownId?: string;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  selectedValue,
  onChange,
  placeholder = "Select an option...",
  searchPlaceholder = "Search cities...",
  className = "",
  error = false,
  disabled = false,
  position = 'below',
  onOpen,
  dropdownId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<'below' | 'above'>('below');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter options based on search term (case-insensitive partial matching)
  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if this dropdown should show icons (only for city/location dropdowns, not ID Type or Nationality)
  const shouldShowIcons = dropdownId !== 'id-type-dropdown' && dropdownId !== 'nationality-dropdown';

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Smart positioning logic (similar to DatePicker in Principal Insured)
  useEffect(() => {
    if (isOpen && position === 'auto' && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 300; // Max dropdown height
      
      // If not enough space below but enough above, position above
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownPosition('above');
      } else {
        setDropdownPosition('below');
      }
    } else if (position === 'above') {
      setDropdownPosition('above');
    } else {
      setDropdownPosition('below');
    }
  }, [isOpen, position]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggleDropdown = () => {
    if (!disabled) {
      const willOpen = !isOpen;
      setIsOpen(willOpen);
      if (willOpen) {
        setSearchTerm('');
        // Notify parent that this dropdown is opening
        if (onOpen) {
          onOpen();
        }
      }
    }
  };

  const handleOptionSelect = (option: string) => {
    onChange(option);
    setIsOpen(false); // Auto-close after selection for single-select
    setSearchTerm('');
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      }
    }
  };

  const getDisplayText = () => {
    return selectedValue || placeholder;
  };

  return (
    <div
      className={`searchable-dropdown ${className} ${isOpen ? 'open' : ''}`}
      ref={dropdownRef}
      data-dropdown-id={dropdownId}
    >
      {/* Main dropdown trigger */}
      <div
        className={`searchable-trigger ${error ? 'error' : ''} ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
        onClick={handleToggleDropdown}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={placeholder}
      >
        <span className={`searchable-display-text ${!selectedValue ? 'placeholder' : ''}`}>
          {getDisplayText()}
        </span>
        <span className={`searchable-arrow ${isOpen ? 'open' : ''}`}>
          ▼
        </span>
      </div>

      {/* Dropdown content */}
      {isOpen && (
        <div
          className={`searchable-dropdown-content ${dropdownPosition === 'above' ? 'position-above' : 'position-below'}`}
          role="listbox"
        >
          {/* Search input */}
          <div className="searchable-search">
            <div className="searchable-search-container">
              <svg className="searchable-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                className="searchable-search-input"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Options list */}
          <div className="searchable-options">
            {filteredOptions.length === 0 ? (
              <div className="searchable-no-options">
                {searchTerm ? 'No matching results found' : 'No options available'}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selectedValue === option;
                
                return (
                  <div
                    key={option}
                    className={`searchable-option ${isSelected ? 'selected' : ''} ${!shouldShowIcons ? 'no-icon' : ''}`}
                    onClick={() => handleOptionSelect(option)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    {/* Conditionally render location icon only for non-ID/nationality dropdowns */}
                    {shouldShowIcons && (
                      <svg className="searchable-location-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                    )}
                    <span className="searchable-option-text">{option}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;